import '../../features/Sales/SalesDashboardCarousel/SalesDashboardCarousel.css'
import { Center, Grid, Loader, Select, Stack, Tabs, Text, TextInput } from "@mantine/core";
import { useContext, useMemo, useState } from "react";
import AppContext from '../../contexts/AppContext/AppContext'
import moment from "moment/moment";
import { IconChartBar, IconTarget, IconUser } from "@tabler/icons-react";
import { useFetch } from "@mantine/hooks";
import AuthContext from "../../contexts/AuthContext";
import FunnelKPICards from "./FunnelKPICards";
import FunnelChart from "./FunnelChart";
import LeadsListModal from "./LeadsListModal";
import FunnelAUBreakdown from "./FunnelAUBreakdown";
import ObjectivesTracker from "./ObjectivesTracker";
import PersonalKPIs from "../PersonalKPIs/PersonalKPIs";
import SellerFocusToday from "../SellerFocusToday/SellerFocusToday";
import LeadAgingTable from "../LeadAgingTable/LeadAgingTable";

const MONTHS = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

const monthNameToIndex = {
    enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
    julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
    ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5,
    jul: 6, ago: 7, sep: 8, sept: 8, oct: 9, nov: 10, dic: 11
};

const normalize = (str = "") =>
    str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

function parseSpanishDateToJSDate(input) {
    if (!input || typeof input !== "string") return null;
    const s = normalize(input);
    const regex = /(\d{1,2})\s+(?:de\s+)?([a-zñáéíóú]+)\s+(?:de\s+|del\s+)?(\d{4})/i;
    const match = s.match(regex);
    if (!match) return null;
    const day = parseInt(match[1], 10);
    const monthStr = match[2];
    const year = parseInt(match[3], 10);
    const monthIndex = monthNameToIndex[monthStr];
    if (monthIndex === undefined) {
        const monthKey = Object.keys(monthNameToIndex).find(key => key.startsWith(monthStr.slice(0, 3)));
        if (!monthKey) return null;
        const idx = monthNameToIndex[monthKey];
        if (idx === undefined) return null;
        const d = new Date(year, idx, day);
        if (Number.isNaN(d.getTime())) return null;
        return d;
    }
    const d = new Date(year, monthIndex, day);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}


export default function SalesDashboardAcademicUnit() {
    const { specialties: specialtiesOptions } = useContext(AppContext);
    const { authToken } = useContext(AuthContext)

    // --- Dashboard original de UAs (se mantiene) ---
    const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/dashboard-academicUnit`;
    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
    };
    const { data: dashboardData, loading } = useFetch(url, config);

    const [showLeadsList, setShowLeadsList] = useState(false)
    const [stageFilter, setStageFilter] = useState(null)

    const handleStageClick = (stage) => {
        setStageFilter(stage);
        setShowLeadsList(true);
    };

    // --- Filtros del embudo GLOBAL ---
    const [funnelFilters, setFunnelFilters] = useState({
        year: moment().year().toString(),
        month: moment().month().toString(),
        country: '',
        academicUnitId: '',
    });

    // Endpoint personal: embudo GLOBAL + personalKpis + focusToday
    const personalUrl = (() => {
        const base = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/dashboard-personal`;
        const params = new URLSearchParams();
        if (funnelFilters.year) params.set('year', funnelFilters.year);
        if (funnelFilters.month !== '') params.set('month', funnelFilters.month);
        if (funnelFilters.country) params.set('country', funnelFilters.country);
        if (funnelFilters.academicUnitId) params.set('academicUnitId', funnelFilters.academicUnitId);
        return `${base}?${params.toString()}`;
    })();

    const { data: personalData, loading: personalLoading } = useFetch(personalUrl, config);
    
    // --- Filtros de UAs (sección 2) ---
    const [filters, setFilters] = useState({
        month: MONTHS[moment().month()],
        year: moment().year().toString(),
        type: '',
        specialty: '',
        search: '',
    });

    const filteredAcademicUnits = useMemo(() => {
        if (!dashboardData?.academicUnits?.length) return [];
        const monthIndex = MONTHS.indexOf(filters.month);
        return dashboardData.academicUnits.filter(item => {
            if (!item?.initialDate) return false;
            if (filters.type) {
                if (item?.type.toLowerCase() !== filters?.type.toLowerCase()) return false;
            }
            if (filters.specialty) {
                const hasSpecialty = item.specialties?.some(s => s._id === filters.specialty);
                if (!hasSpecialty) return false;
            }
            if (filters.search) {
                if (!item?.name.toUpperCase().includes(filters?.search.toUpperCase())) return false;
            }
            const parsed = parseSpanishDateToJSDate(item.initialDate);
            let dateToUse = parsed;
            if (!parsed) {
                const normalized = normalize(item.initialDate);
                const m = moment(normalized, "D [de] MMMM [del] YYYY", true);
                if (m.isValid()) {
                    dateToUse = m.toDate();
                } else {
                    return false;
                }
            }
            if (dateToUse.getFullYear().toString() !== filters.year) return false;
            if (filters.month && filters.month !== '') {
                if (dateToUse.getMonth() !== monthIndex) return false;
            }
            return true;
        });
    }, [dashboardData, filters]);

    // Transformar datos al formato que espera ObjectivesTracker
    const objectivesData = useMemo(() => filteredAcademicUnits.map(item => ({
        _id: item._id,
        auName: item.name,
        auType: item.type,
        auInitialDate: item.initialDate,
        inscriptionTarget: item.inscription_target,
        equivalentAt100: item.equalTo100,
        totalPayments: item.paymentsCount,
        progressPercent: item.inscription_target > 0
            ? Math.round((item.equalTo100 / item.inscription_target) * 1000) / 10
            : 0,
    })), [filteredAcademicUnits]);

    if (loading && personalLoading) return <Center>
        <Stack align='center'>
            <Loader />
            <h3>Cargando ventas...</h3>
        </Stack>
    </Center>

    return <>
        <Tabs defaultValue="embudo" keepMounted={false}>
            <Tabs.List mb="md">
                <Tabs.Tab value="embudo" leftSection={<IconChartBar size={16} />}>
                    Embudo
                </Tabs.Tab>
                <Tabs.Tab value="objetivos" leftSection={<IconTarget size={16} />}>
                    Objetivos
                </Tabs.Tab>
                {/* <Tabs.Tab value="mipanel" leftSection={<IconUser size={16} />}>
                    Mi Panel
                </Tabs.Tab> */}
            </Tabs.List>

            {/* ===== TAB 1: EMBUDO ===== */}
            <Tabs.Panel value="embudo">
                <Grid gap={8} mb="md">
                    <Grid.Col span={{ base: 6, sm: 3 }}>
                        <Select
                            label='Año'
                            allowDeselect={false}
                            value={funnelFilters.year}
                            onChange={(value) => {
                                if (!value) return;
                                setFunnelFilters(prev => ({ ...prev, year: value }));
                            }}
                            data={[moment().year().toString(), (moment().year() - 1).toString()]}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 3 }}>
                        <Select
                            label='Mes'
                            allowDeselect={false}
                            value={funnelFilters.month}
                            onChange={(value) => {
                                setFunnelFilters(prev => ({ ...prev, month: value }));
                            }}
                            data={[
                                { value: '', label: "Todos" },
                                ...MONTHS.map((m, idx) => ({ value: idx.toString(), label: m }))
                            ]}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 3 }}>
                        <Select
                            label='País'
                            allowDeselect={false}
                            searchable
                            clearable
                            value={funnelFilters.country}
                            onChange={(value) => {
                                setFunnelFilters(prev => ({ ...prev, country: value || '' }));
                            }}
                            data={[
                                { value: '', label: 'Todos' },
                                ...(personalData?.distinctCountries || []).map(c => ({ value: c, label: c }))
                            ]}
                            placeholder="Filtrar por país"
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, sm: 3 }}>
                        <Select
                            label='Capacitación'
                            allowDeselect={false}
                            searchable
                            clearable
                            value={funnelFilters.academicUnitId}
                            onChange={(value) => {
                                setFunnelFilters(prev => ({ ...prev, academicUnitId: value || '' }));
                            }}
                            data={[
                                { value: '', label: 'Todas' },
                                ...(personalData?.distinctAcademicUnits || []).map(au => ({
                                    value: au._id,
                                    label: au.auName
                                }))
                            ]}
                            placeholder="Filtrar por capacitación"
                        />
                    </Grid.Col>
                </Grid>

                {personalLoading ? (
                    <Center py="xl">
                        <Stack align='center' gap={4}>
                            <Loader size="sm" />
                            <span className="salesDashboardCarousel-stats_table-content">Cargando...</span>
                        </Stack>
                    </Center>
                ) : personalData ? (
                    <Stack gap="md">
                        <FunnelKPICards kpis={personalData.kpis} onViewLeads={() => { setStageFilter(null); setShowLeadsList(true); }} />
                        <FunnelChart funnel={personalData.funnel} onStageClick={handleStageClick} />
                        <LeadAgingTable agingByStage={personalData.agingByStage} />
                        {!funnelFilters.academicUnitId && (
                            <FunnelAUBreakdown byAcademicUnit={personalData.byAcademicUnit} />
                        )}
                    </Stack>
                ) : null}
            </Tabs.Panel>

            {/* ===== TAB 2: OBJETIVOS ===== */}
            <Tabs.Panel value="objetivos">
                <Stack gap="md" mt="sm">
                    {/* Filtros */}
                    <Grid gap={8}>
                        <Grid.Col span={{ base: 6, sm: 3 }}>
                            <Select
                                label='Año'
                                allowDeselect={false}
                                value={filters.year}
                                onChange={(value) => {
                                    if (!value) return;
                                    setFilters(prev => ({ ...prev, year: value }));
                                }}
                                data={[moment().year().toString(), (moment().year() - 1).toString()]}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 6, sm: 3 }}>
                            <Select
                                label='Mes'
                                allowDeselect={false}
                                value={filters.month}
                                onChange={(value) => {
                                    setFilters(prev => ({ ...prev, month: value }));
                                }}
                                data={[{ value: '', label: "Todos" }, ...MONTHS]}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 6, sm: 3 }}>
                            <Select
                                allowDeselect={false}
                                label='Especialidad'
                                value={filters.specialty}
                                onChange={(value) => {
                                    setFilters(prev => ({ ...prev, specialty: value || '' }));
                                }}
                                data={[{ value: '', label: 'Todas' }, ...specialtiesOptions]}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 6, sm: 3 }}>
                            <Select
                                allowDeselect={false}
                                label='Tipo'
                                value={filters.type}
                                onChange={(value) => {
                                    setFilters(prev => ({ ...prev, type: value || '' }));
                                }}
                                data={[
                                    { value: '', label: 'Todos' },
                                    { value: 'curso', label: 'Curso' },
                                    { value: 'diplomado', label: 'Diplomado' },
                                    { value: 'posgrado', label: 'Posgrado' },
                                    { value: 'webinar', label: 'Webinar' },
                                    { value: 'masterclass', label: 'Masterclass' },
                                    { value: 'taller', label: 'Taller' },
                                    { value: 'bootcamp', label: 'Bootcamp' },
                                ]}
                            />
                        </Grid.Col>
                    </Grid>
                    <TextInput
                        label='Buscar por nombre'
                        placeholder="Filtrar capacitaciones..."
                        onChange={({ target }) => {
                            setFilters(prev => ({ ...prev, search: target.value.trim() }))
                        }}
                    />
                    {loading ? (
                        <Stack align="center" py="xl">
                            <Loader size="sm" />
                            <Text size="sm" c="dimmed">Cargando objetivos...</Text>
                        </Stack>
                    ) : (
                        <ObjectivesTracker objectives={objectivesData} />
                    )}
                </Stack>
            </Tabs.Panel>

            {/* ===== TAB 3: MI PANEL ===== */}
            {/* <Tabs.Panel value="mipanel">
                {personalLoading ? (
                    <Center py="xl">
                        <Stack align='center' gap={4}>
                            <Loader size="sm" />
                            <span className="salesDashboardCarousel-stats_table-content">Cargando...</span>
                        </Stack>
                    </Center>
                ) : personalData ? (
                    <Stack gap="md" mt="md">
                        <PersonalKPIs personalKpis={personalData.personalKpis} />
                        <SellerFocusToday focusToday={personalData.focusToday} />
                    </Stack>
                ) : null}
            </Tabs.Panel> */}
        </Tabs>

        {/* Modal de listado de leads (compartido entre tabs) */}
        <LeadsListModal
            opened={showLeadsList}
            onClose={() => { setShowLeadsList(false); setStageFilter(null); }}
            filters={{ ...funnelFilters, funnelStage: stageFilter }}
        />
    </>
}


